import { ChevronDown, ChevronRight, Edit2 } from 'lucide-react';
import { useState } from 'react';

import { useGetHourlyTuitionBilling } from '~entities/payment/api/hourly-tuition/get-hourly-tuition-billing';

import { Badge } from '~shared/shadcn/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

import { DeleteAlertDialog } from './delete-dialog-alert';
import { EditBillingPlanDialog } from './edit-hourly-tuition-form';

interface BillingRange {
  _id: string;
  minMinute: number;
  maxMinute: number;
  flatRate: number;
}

interface BillingPlan {
  _id: string;
  name: string;
  studioId: string;
  ranges: Array<BillingRange>;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export function HourlyTuitionRateTable() {
  const { data: hourlyTuitionBilling } = useGetHourlyTuitionBilling();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan | null>(null);

  const handleEditClick = (plan: BillingPlan) => {
    setSelectedPlan(plan);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedPlan(null);
  };

  const plans: Array<BillingPlan> = Array.isArray(hourlyTuitionBilling)
    ? hourlyTuitionBilling.map((plan) => ({
        ...plan,
        createdAt: plan.createdAt.toString(),
        updatedAt: plan.updatedAt.toString(),
      }))
    : [];

  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(
    new Set(plans.map((plan) => plan._id)),
  );

  const togglePlan = (planId: string) => {
    const newExpanded = new Set(expandedPlans);
    if (newExpanded.has(planId)) {
      newExpanded.delete(planId);
    } else {
      newExpanded.add(planId);
    }
    setExpandedPlans(newExpanded);
  };

  return (
    <div className='space-y-4'>
      {plans.map((plan: BillingPlan) => (
        <Card key={plan._id} className=''>
          <CardHeader className='pb-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => {
                    togglePlan(plan._id);
                  }}
                  className='rounded p-1 transition-colors hover:bg-gray-100'
                >
                  {expandedPlans.has(plan._id) ? (
                    <ChevronDown className='size-4 text-gray-600' />
                  ) : (
                    <ChevronRight className='size-4 text-gray-600' />
                  )}
                </button>
                <CardTitle className='text-xl font-semibold capitalize text-gray-900'>
                  {plan.name}
                </CardTitle>
              </div>
              <div className='flex items-center gap-2'>
                <Badge
                  variant='secondary'
                  className='border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100'
                >
                  {plan.ranges.length} tier
                  {plan.ranges.length !== 1 ? 's' : ''}
                </Badge>
                <Edit2
                  className='size-4 cursor-pointer text-gray-500 hover:text-gray-700'
                  onClick={() => {
                    handleEditClick(plan);
                  }}
                />
                <DeleteAlertDialog billingId={plan._id} />
              </div>
            </div>
          </CardHeader>

          {expandedPlans.has(plan._id) && (
            <CardContent>
              <div className='space-y-1'>
                <h3 className='text-base font-semibold text-gray-900'>
                  Tuition Rate Ranges
                </h3>

                <div className='grid grid-cols-3 gap-4 border-b border-gray-200 pb-2'>
                  <div className='text-xs font-semibold uppercase tracking-wide text-gray-700'>
                    Min Minutes
                  </div>
                  <div className='text-xs font-semibold uppercase tracking-wide text-gray-700'>
                    Max Minutes
                  </div>
                  <div className='text-xs font-semibold uppercase tracking-wide text-gray-700'>
                    Flat Rate
                  </div>
                </div>

                <div className='space-y-2'>
                  {plan.ranges.map((range: BillingRange) => (
                    <div
                      key={range._id}
                      className='grid grid-cols-3 items-center gap-4 border-b border-gray-100 py-2 last:border-b-0'
                    >
                      <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900'>
                        {range.minMinute}
                      </div>
                      <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900'>
                        {range.maxMinute}
                      </div>
                      <div className='rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-semibold text-green-800'>
                        $ {range.flatRate}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      ))}
      <EditBillingPlanDialog
        plan={selectedPlan}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={(updatedPlan) => {
          setIsDialogOpen(false);
          setSelectedPlan(null);
          console.log('Updated plan:', updatedPlan);
        }}
      />
    </div>
  );
}
